import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import { useOpenInfoBottomSheet } from '@/components/InfoBottomSheet/hooks/useOpenInfoBottomSheet';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { AmountWithInfo } from './AmountWithInfo';

type Props = {
    currency: string;
    highBidEstimate: number;
    id: string;
    lowBidEstimate: number;
    /**
     * DetailsRow's wrapper already margins direct currency divs (`[&>div]:mx-2xs`).
     * That margin is the gap before the icon, so the icon stays unmargined.
     * Cards have no such wrapper, so the icon carries `ml-2xs`.
     */
    spaceAmountFromParent?: boolean;
};

export const FlaggedEstimateLabel = ({
    currency,
    highBidEstimate,
    id,
    lowBidEstimate,
    spaceAmountFromParent = false,
}: Props) => {
    const formatMessage = useAppFormatMessage();
    const isMobile = useWindowIsMobile();
    const openInfoBottomSheet = useOpenInfoBottomSheet();

    if (!lowBidEstimate && !highBidEstimate) {
        return (
            <FormattedMessage
                id="short_estimate_value"
                values={{ number: '—' }}
            />
        );
    }

    if (!lowBidEstimate || !highBidEstimate) {
        return formatMessage(['itemCardEstimate', isMobile ? 'shortSingle' : 'single'], {
            values: {
                number: (
                    <AmountWithInfo
                        currency={currency}
                        id={id}
                        key="estimate-single"
                        onMobileClick={openInfoBottomSheet}
                        spaceAmountFromParent={spaceAmountFromParent}
                        value={lowBidEstimate || highBidEstimate}
                    />
                ),
            },
        });
    }

    return formatMessage(['itemCardEstimate', isMobile ? 'shortRange' : 'range'], {
        values: {
            high: (
                <AmountWithInfo
                    currency={currency}
                    id={id}
                    key="estimate-high"
                    onMobileClick={openInfoBottomSheet}
                    spaceAmountFromParent={spaceAmountFromParent}
                    value={highBidEstimate}
                />
            ),
            low: (
                <FormattedCurrency
                    key="estimate-low"
                    noFraction
                    sourceCurrency={currency}
                    value={lowBidEstimate}
                />
            ),
        },
    });
};
